import { DiscoveryFilter, DiscoveryItem } from '@teko/discovery';
import { DiscoveryBlock } from 'types';
import get from 'lodash.get';
import { LinkProps } from 'next/link';
import { BannerEntity } from '@teko/ecommerce';
import { getTrackingData, PromoteBlockProps } from '@teko/teko-ecommerce';
import { ServiceContainer } from './service-provider';
import { logger } from '@teko/log';
import slug from '@teko/slug';

const listingAndDiscoveryKeysMap: any = {
  priceGte: 'price_gte',
  priceLte: 'price_lte',
};
const listingAndDiscoveryValuesMap: any = {
  ORDER_BY_DESCENDING: 'DESC',
  ORDER_BY_ASCENDING: 'ASC',
};
const cleanObject = (object: any) => {
  const newObj: any = {};
  Object.keys(object).forEach((key) => {
    if (object[key]) {
      if (
        !Array.isArray(object[key]) ||
        (Array.isArray(object[key]) && object[key].length)
      ) {
        newObj[key] = object[key];
      }
    }
  });
  return newObj;
};
export const convertObjectDiscoveryToQueryStringSearch = (
  params: any,
): string => {
  if (!params) return '/';
  const newParams = cleanObject(params);
  return (
    '/search?' +
    Object.keys({ ...newParams })
      .map((key) => {
        return (
          (listingAndDiscoveryKeysMap[key] || key) +
          '=' +
          (listingAndDiscoveryValuesMap[newParams[key]] || newParams[key])
        );
      })
      .join('&')
      .concat('&router=productListing')
  );
};

export const cookDiscoveryFilter = (filter: DiscoveryFilter) => {
  if (!filter) return {};
  if (filter?.attributes.length) {
    filter.attributes.map(
      (el) => (filter[`attributes.${el.code}`] = el.optionIds.join()),
    );
    const { attributes: tbr, ...newFilter } = filter;
    return newFilter;
  }
  return filter;
};
export const findBlockByType = (
  type: string,
  blocks: DiscoveryBlock[],
): DiscoveryBlock => {
  return blocks && blocks.find((block) => block.type === type);
};
const getUrl = (item: DiscoveryItem): string => {
  let url = '';
  if (item.url) {
    url = item.url;
  } else {
    url = convertObjectDiscoveryToQueryStringSearch({
      ...cookDiscoveryFilter(item?.params?.filter),
      ...get(item, 'params.sorting', {}),
    });
  }
  return url;
};
const getLinkHref = (
  url: string,
  paramsObject: {},
  title: string,
): LinkProps => {
  let href = null;
  if (url) {
    if (url.startsWith('/landing/')) {
      href = {
        pathname: '/landing/[id]',
        query: {
          id: url.substring('/landing/'.length),
        },
      };
    } else {
      href = url;
    }
  } else {
    href = {
      pathname: '/search',
      query: {
        ...paramsObject,
        pageTitle: title,
        router: 'productListing',
      },
    };
  }
  return { href };
};
export const transformBlockItemTypeSku = (block: DiscoveryBlock) => {
  const { fetchParams } = block.content;
  block.cookedData = {
    queryString: convertObjectDiscoveryToQueryStringSearch({
      ...cookDiscoveryFilter(fetchParams?.filter),
      ...fetchParams.sorting,
    }),
    paramsObject: cleanObject({
      ...cookDiscoveryFilter(fetchParams?.filter),
      ...(fetchParams.sorting || {}),
      blockId: block.id,
    }),
  };
  return block;
};
export const transformBanner = (block: DiscoveryBlock) => {
  const banners: BannerEntity[] = block.content.items.map((item) => {
    const paramsObject = cleanObject({
      ...cookDiscoveryFilter(item?.params?.filter),
      ...get(item, 'params.sorting', {}),
      blockId: block.id,
      itemId: item.id,
    });
    return {
      id: item.id,
      targetPath: getUrl(item),
      image: item.imageUrl,
      campaignName: item.label,
      description: item.subLabel,
      paramsObject,
      url: item.url,
      thumbnail: item.thumbnail,
      link: getLinkHref(item.url, paramsObject, item.label),
      imageSize: item.imageSize,
    };
  });
  block.cookedData = banners || [];
  return block;
};
export const transformBlockMixBanner = (block: DiscoveryBlock) => {
  const firstItem = block.content.items[0];
  const trackingRegionName = 'categoriesBlockBrand';
  block.cookedData = {
    header: {
      title: block.label,
      link: {
        href: getUrl(firstItem),
      },
    },
    banner: {
      src: firstItem.imageUrl,
      alt: firstItem.label || firstItem.subLabel,
      tracking: getTrackingData({
        isTrackContent: true,
        regionName: trackingRegionName,
        contentName: firstItem.label,
        target: getUrl(firstItem),
      }),
      link: {
        href: getUrl(firstItem),
      },
    },
    contentItems: block.content.items.slice(1, 5).map((category) => ({
      name: category.label,
      description: category.subLabel,
      image: {
        src: category.imageUrl,
        alt: category.label || category.subLabel,
      },
      link: {
        href: getUrl(category),
      },
    })),
    bottomImages: block.content.items.slice(5, 11).map((brand, index) => ({
      src: brand.imageUrl,
      alt: brand.label || brand.subLabel,
      tracking: getTrackingData({
        isTrackContent: true,
        regionName: trackingRegionName,
        contentName: brand.label,
        index,
        target: getUrl(brand),
      }),
      link: {
        href: getUrl(brand),
      },
    })),
  } as PromoteBlockProps;
  return block;
};
export const transformBlockHotDeal = (block: DiscoveryBlock) => {
  block.cookedData = block.content.items.map((el) => {
    return {
      ...el,
      name: el.label,
      paramsObject: cleanObject({
        ...cookDiscoveryFilter(el?.params?.filter),
        ...get(el, 'params.sorting', {}),
        blockId: block.id,
        itemId: el.id,
      }),
    };
  });
  return block;
};
export const transformBlockListingTab = (block: DiscoveryBlock) => {
  const categoryList: any[] = [];
  block.content.items.forEach((item: DiscoveryItem) => {
    const obj = {
      id: item.id,
      name: item.label,
      paramsObject: cleanObject({
        ...cookDiscoveryFilter(item?.params?.filter),
        ...get(item, 'params.sorting', {}),
        blockId: block.id,
        itemId: item.id,
      }),
    };
    categoryList.push(obj);
  });
  block.cookedData = categoryList;
  return block;
};
export const transformBlock = (block: DiscoveryBlock) => {
  if (block.itemType === 'SKU') return transformBlockItemTypeSku(block);
  else if (
    block.itemType === 'BANNER' ||
    [
      'HORIZONTAL_HIGHLIGHTED_BANNER',
      'RECOMMENDED_CATEGORY',
      'RECOMMENDED_BRANDS',
      'PROMOTE_CATEGORY_MENU',
      'HORIZONTAL_2_BANNER_MOBILE',
    ].includes(block.type)
  )
    return transformBanner(block);
  else if (block.type === 'MIX_BANNER') return transformBlockMixBanner(block);
  else if (
    block.type === 'COUNT_DOWN_PROMO_BIG_SKU' ||
    block.type === 'COUNT_DOWN_PROMO_TAB_MOBILE'
  )
    return transformBlockHotDeal(block);
  else if (block.type === 'LISTING_TAB') return transformBlockListingTab(block);
  else return block;
};

export const isDefaultBlock = (
  blocks: DiscoveryBlock[],
  device: 'desktop' | 'mobile',
) => {
  const mainSkuList = findBlockByType(
    device === 'desktop' ? 'MAIN_SKU_LIST' : 'LIST_SKU_MOBILE',
    blocks,
  );
  return blocks.length === 2 && !!mainSkuList;
};

export const getBodyDiscoveryDefaultBlock = (
  blocks: DiscoveryBlock[],
  device: 'desktop' | 'mobile',
) => {
  if (!isDefaultBlock(blocks, device)) return null;
  const mainSkuList = findBlockByType(
    device === 'desktop' ? 'MAIN_SKU_LIST' : 'LIST_SKU_MOBILE',
    blocks,
  );
  const fetchParams = {
    ...mainSkuList.content.fetchParams,
    blockId: mainSkuList.id,
  };
  const filterParams = {
    ...fetchParams.filter,
    ...fetchParams.sorting,
    blockId: mainSkuList.id,
  } as any;
  return cleanObject(filterParams);
};

export const fetchDataBlockSSR = async (
  context: any,
  blocks: DiscoveryBlock[],
) => {
  const simpleProductBlocks = blocks.filter(
    (b) =>
      [
        'FLASH_SALE',
        'PROMOTE_SELLER',
        'BEST_SELLING_PRODUCT',
        'LIST_SKU_MOBILE',
      ].includes(b.type) && b.itemType === 'SKU',
  );
  const partialRenderedBlocks = blocks.filter((b) =>
    ['COUNT_DOWN_PROMO_TAB_MOBILE', 'LISTING_TAB'].includes(b.type),
  );
  const { discoveryService } = new ServiceContainer(
    (context.req as any).tenantEnv,
  );

  const mapProducts = await Promise.all([
    ...simpleProductBlocks.map(async (block) => {
      const fetchParams = {
        ...block?.content?.fetchParams,
      };
      const filterParams = {
        ...fetchParams.filter,
        ...fetchParams.sorting,
        blockId: block.id,
      };
      const products = await discoveryService
        .search({
          ...cleanObject(filterParams),
          terminalCode: (context.req as any).tenantEnv.terminal,
          itemsPerPage: 20,
          location: (context.req as any).location,
        })
        .then((res) => res?.result?.products || [])
        .catch((error) => {
          logger.error(error);
          return [];
        });
      return {
        [block.id]: products,
      };
    }),
    ...partialRenderedBlocks.map(async (block) => {
      // paramObject contant: blockId, itemId and other filter of discovery
      const paramsObject = block?.cookedData?.[0]?.paramsObject;
      if (!paramsObject || !paramsObject?.blockId || !paramsObject?.itemId)
        return;
      const products = await discoveryService
        .search({
          ...paramsObject,
          terminalCode: (context.req as any).tenantEnv.terminal,
          itemsPerPage: 20,
          location: (context.req as any).location,
        })
        .then((res) => res?.result?.products || [])
        .catch((error) => {
          logger.error(error);
          return [];
        });
      return {
        [`${paramsObject.blockId}_${paramsObject.itemId}`]: products,
      };
    }),
  ]);
  return mapProducts
    .filter(Boolean)
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});
};

export const getBlockDomId = (block: DiscoveryBlock): string =>
  block.label ? slug(block.label) : `${block.name}-${block.id}`;
